import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useToast } from "@/hooks/use-toast";
import { Loader2, FileText, Download, X, ChevronLeft, ChevronRight, ExternalLink, Edit3, Save, Eye, RefreshCw } from 'lucide-react';

interface DocumentViewerProps {
  document: {
    id: string;
    file_name: string;
    file_path: string;
    document_type?: string;
  };
  isOpen: boolean;
  onClose: () => void;
}

export const DocumentViewer = ({ document, isOpen, onClose }: DocumentViewerProps) => {
  const [loading, setLoading] = useState(true);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'view' | 'edit'>('view');
  const [editUrl, setEditUrl] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && document) {
      loadDocument();
    }
  }, [isOpen, document]);

  const loadDocument = async () => {
    setLoading(true);
    setError(null);

    try {
      // Gerar URL assinada para o documento
      const { data, error } = await supabase.storage
        .from('case-documents')
        .createSignedUrl(document.file_path, 3600); // 1 hora

      if (error) throw error;

      setDocumentUrl(data.signedUrl);
    } catch (err: any) {
      console.error('Erro ao carregar documento:', err);
      setError('Erro ao carregar o documento. Tente novamente.');
      toast({
        title: "Erro",
        description: "Não foi possível carregar o documento.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    try {
      const { data, error } = await supabase.storage
        .from('case-documents')
        .createSignedUrl(document.file_path, 60);

      if (error) throw error;

      // Criar link de download
      const link = window.document.createElement('a');
      link.href = data.signedUrl;
      link.download = document.file_name;
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);

      toast({
        title: "Download iniciado",
        description: "O arquivo será baixado em breve.",
      });
    } catch (err: any) {
      toast({
        title: "Erro no download",
        description: "Não foi possível baixar o arquivo.",
        variant: "destructive",
      });
    }
  };

  const startEditing = async () => {
    if (!documentUrl) return;

    try {
      // Usar Microsoft Office Online - mais permissivo com URLs externas
      const officeEditUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(documentUrl)}&wdStartOn=1`;
      window.open(officeEditUrl, '_blank');
      
      toast({
        title: "Editor aberto",
        description: "O editor do Office Online foi aberto em uma nova aba.",
      });
    } catch (err: any) {
      toast({
        title: "Erro ao abrir editor",
        description: "Não foi possível abrir o editor.",
        variant: "destructive",
      });
    }
  };

  const saveDocument = async () => {
    if (!editUrl) return;

    try {
      // Aqui você pode implementar a lógica para salvar as mudanças
      // Por enquanto, vamos mostrar uma mensagem de sucesso
      toast({
        title: "Documento salvo",
        description: "As mudanças foram salvas com sucesso.",
      });
      
      // Recarregar o documento para mostrar as mudanças
      await loadDocument();
      setViewMode('view');
      setIsEditing(false);
      setEditUrl(null);
    } catch (err: any) {
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar as mudanças.",
        variant: "destructive",
      });
    }
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEditUrl(null);
    setViewMode('view');
    toast({
      title: "Edição cancelada",
      description: "As mudanças não foram salvas.",
    });
  };

  const openInGoogleDocs = () => {
    if (!documentUrl) return;
    
    // Usar Google Docs Viewer para DOC/DOCX
    const googleDocsUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(documentUrl)}&embedded=true`;
    window.open(googleDocsUrl, '_blank');
  };

  const openInOfficeOnline = () => {
    if (!documentUrl) return;
    
    // Usar Microsoft Office Online
    const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(documentUrl)}`;
    window.open(officeUrl, '_blank');
  };

  const openInGoogleDocsEdit = () => {
    if (!documentUrl) return;
    
    // Usar Google Docs para edição - versão que funciona melhor
    const googleDocsEditUrl = `https://docs.google.com/document/create?usp=upload_embed&url=${encodeURIComponent(documentUrl)}`;
    window.open(googleDocsEditUrl, '_blank');
  };

  const openInOfficeOnlineEdit = () => {
    if (!documentUrl) return;
    
    // Usar Microsoft Office Online para edição
    const officeEditUrl = `https://www.office.com/launch/word?auth=2&src=${encodeURIComponent(documentUrl)}`;
    window.open(officeEditUrl, '_blank');
  };

  const startEditingDirect = async () => {
    if (!documentUrl) return;

    try {
      // Usar Microsoft Office Online - mais permissivo com URLs externas
      const officeEditUrl = `https://www.office.com/launch/word?auth=2&src=${encodeURIComponent(documentUrl)}`;
      window.open(officeEditUrl, '_blank');
      
      toast({
        title: "Editor aberto",
        description: "O editor do Office Online foi aberto em uma nova aba.",
      });
    } catch (err: any) {
      toast({
        title: "Erro ao abrir editor",
        description: "Não foi possível abrir o editor.",
        variant: "destructive",
      });
    }
  };

  const isPDF = document.document_type === 'application/pdf';
  const isImage = document.document_type?.startsWith('image/');
  const isText = document.document_type === 'text/plain';
  const isWordDoc = document.document_type === 'application/msword' || 
                   document.document_type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
                   document.document_type === 'application/vnd.ms-word' ||
                   document.document_type === 'application/vnd.ms-word.document.12' ||
                   document.file_name.toLowerCase().endsWith('.doc') ||
                   document.file_name.toLowerCase().endsWith('.docx');

  const renderDocumentContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-96">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span className="ml-2">Carregando documento...</span>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-center">
          <FileText className="w-16 h-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Erro ao carregar</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={loadDocument} variant="outline">
            Tentar Novamente
          </Button>
        </div>
      );
    }

    if (!documentUrl) {
      return (
        <div className="flex flex-col items-center justify-center h-96 text-center">
          <FileText className="w-16 h-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Documento não encontrado</h3>
          <p className="text-gray-600">O arquivo não pôde ser carregado.</p>
        </div>
      );
    }

    // Renderizar baseado no tipo de arquivo
    if (isPDF) {
      return (
        <div className="w-full h-full">
          <iframe
            src={`${documentUrl}#toolbar=0&navpanes=0&scrollbar=0`}
            className="w-full h-full border-0"
            title={document.file_name}
          />
        </div>
      );
    }

    if (isImage) {
      return (
        <div className="flex justify-center h-full">
          <img
            src={documentUrl}
            alt={document.file_name}
            className="max-w-full max-h-full object-contain"
          />
        </div>
      );
    }

    if (isText) {
      return (
        <div className="w-full h-full">
          <iframe
            src={documentUrl}
            className="w-full h-full border-0"
            title={document.file_name}
          />
        </div>
      );
    }

    // Para DOC/DOCX, mostrar visualização direta no modal
    if (isWordDoc) {
      if (viewMode === 'view') {
        return (
          <div className="w-full h-full">
            <iframe
              src={`https://docs.google.com/viewer?url=${encodeURIComponent(documentUrl)}&embedded=true`}
              className="w-full h-full border-0"
              title={document.file_name}
            />
          </div>
        );
      } else {
        // Modo de edição
        if (isEditing && editUrl) {
          return (
            <div className="w-full h-full flex flex-col">
              <div className="flex-shrink-0 p-4 bg-gray-50 border-b">
                <div className="flex items-center justify-between">
                                     <div className="flex items-center gap-2">
                     <Edit3 className="w-4 h-4 text-blue-600" />
                     <span className="font-medium">Modo de Edição - Google Docs</span>
                   </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" onClick={saveDocument} variant="default">
                      <Save className="w-4 h-4 mr-2" />
                      Salvar
                    </Button>
                    <Button size="sm" onClick={cancelEditing} variant="outline">
                      Cancelar
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex-1">
                <iframe
                  src={editUrl}
                  className="w-full h-full border-0"
                  title={document.file_name}
                />
              </div>
            </div>
          );
        } else {
          // Opções de edição
          return (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <FileText className="w-16 h-16 text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Editar Documento</h3>
              <p className="text-gray-600 mb-6">
                Escolha uma das opções abaixo para editar o documento:
              </p>
              
              <div className="space-y-3 w-full max-w-sm">
                                 <Button 
                   onClick={startEditing} 
                   className="w-full"
                   variant="default"
                 >
                   <Edit3 className="w-4 h-4 mr-2" />
                   Editar no Office Online (Nova Aba)
                 </Button>
                 
                 <Button 
                   onClick={startEditingDirect} 
                   className="w-full"
                   variant="default"
                 >
                   <Edit3 className="w-4 h-4 mr-2" />
                   Editar no Office Online Direto (Nova Aba)
                 </Button>
                
                <Button 
                  onClick={openInGoogleDocsEdit} 
                  className="w-full"
                  variant="outline"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Editar no Google Docs
                </Button>
                
                <Button 
                  onClick={openInOfficeOnlineEdit} 
                  className="w-full"
                  variant="outline"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Editar no Office Online
                </Button>
                
                <Button 
                  onClick={handleDownload} 
                  className="w-full"
                  variant="outline"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar para Editar
                </Button>
              </div>
              
                             <p className="text-xs text-gray-500 mt-4">
                 * Office Online é mais compatível com URLs do Supabase
               </p>
            </div>
          );
        }
      }
    }

    // Para outros tipos de arquivo
    return (
      <div className="flex flex-col items-center justify-center h-96 text-center">
        <FileText className="w-16 h-16 text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Visualização não disponível</h3>
        <p className="text-gray-600 mb-4">
          Este tipo de arquivo ({document.document_type}) não pode ser visualizado diretamente.
          Faça o download para abrir no seu computador.
        </p>
        <Button onClick={handleDownload} className="mb-2">
          <Download className="w-4 h-4 mr-2" />
          Baixar Arquivo
        </Button>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md w-full h-[95vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center">
              <FileText className="w-5 h-5 mr-2" />
              {document.file_name}
            </DialogTitle>
            <div className="flex items-center gap-2">
              {isWordDoc && !isEditing && (
                <>
                  <Button 
                    variant={viewMode === 'view' ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={() => setViewMode('view')}
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    Visualizar
                  </Button>
                  <Button 
                    variant={viewMode === 'edit' ? 'default' : 'outline'} 
                    size="sm" 
                    onClick={() => setViewMode('edit')}
                  >
                    <Edit3 className="w-4 h-4 mr-2" />
                    Editar
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" onClick={handleDownload}>
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
              <Button variant="outline" size="sm" onClick={onClose}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden">
          {renderDocumentContent()}
        </div>
      </DialogContent>
    </Dialog>
  );
};
